import { useState } from "react";
import {
  Box,
  FormControl,
  IconButton,
  Slider,
  SliderFilledTrack,
  SliderMark,
  SliderThumb,
  SliderTrack,
  Tooltip,
  useColorModeValue,
} from "@chakra-ui/react";
import { CollecticonArrowSemiSpinCcw } from "@devseed-ui/collecticons-chakra";
import { DEFAULT_BRIGHTNESS, DEFAULT_CONTRAST } from "../settings";

const imageControlLabelStyles = {
  mt: "2",
  opacity: "0.75",
  fontSize: "xs",
};

type Props = {
  brightness: number;
  setBrightness: (value: number) => void;
  contrast: number;
  setContrast: (value: number) => void;
};

function ImageControls({
  brightness,
  setBrightness,
  contrast,
  setContrast,
}: Props) {
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const bgColor = useColorModeValue("primary.700", "primary.300");

  return (
    <Box display="flex" flexDir="column" gap="6">
      <FormControl>
        <Box
          fontWeight="semibold"
          fontSize="sm"
          id="brightness"
          display="flex"
          alignItems="center"
          gap="2"
        >
          Brightness
          <IconButton
            aria-label="reset brightness"
            variant="ghost"
            colorScheme="gray"
            size="xs"
            icon={<CollecticonArrowSemiSpinCcw width="10px" height="10px" />}
            isDisabled={brightness === DEFAULT_BRIGHTNESS}
            title="Reset brightness values to default"
            onClick={() => setBrightness(DEFAULT_BRIGHTNESS)}
          />
        </Box>
        <Slider
          name="image-brightness"
          aria-labelledby="brightness"
          colorScheme="primary"
          step={0.125}
          min={0.75}
          max={4}
          value={brightness}
          onChange={(value: number) => setBrightness(value)}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <SliderMark value={0.75} {...imageControlLabelStyles}>
            Darker
          </SliderMark>
          <SliderMark value={3.5} {...imageControlLabelStyles}>
            Brighter
          </SliderMark>
          <SliderTrack>
            <SliderFilledTrack />
          </SliderTrack>
          <Tooltip
            hasArrow
            bg={bgColor}
            placement="top"
            isOpen={showTooltip}
            label={`${brightness}`}
          >
            <SliderThumb />
          </Tooltip>
        </Slider>
      </FormControl>
      <FormControl>
        <Box
          fontWeight="semibold"
          fontSize="sm"
          id="contrast"
          display="flex"
          alignItems="center"
          gap="2"
        >
          Contrast
          <IconButton
            aria-label="reset contrast"
            variant="ghost"
            colorScheme="gray"
            size="xs"
            icon={<CollecticonArrowSemiSpinCcw width="10px" height="10px" />}
            isDisabled={contrast === DEFAULT_CONTRAST}
            title="Reset contrast values to default"
            onClick={() => setContrast(DEFAULT_CONTRAST)}
          />
        </Box>
        <Slider
          name="image-contrast"
          arias-labeledby="contrast"
          colorScheme="primary"
          step={0.5}
          min={1}
          max={20}
          value={contrast}
          onChange={(value: number) => setContrast(value)}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <SliderMark value={1} {...imageControlLabelStyles}>
            Less
          </SliderMark>
          <SliderMark value={18.25} {...imageControlLabelStyles}>
            More
          </SliderMark>
          <SliderTrack>
            <SliderFilledTrack />
          </SliderTrack>
          <Tooltip
            hasArrow
            bg={bgColor}
            placement="top"
            isOpen={showTooltip}
            label={`${contrast}`}
          >
            <SliderThumb />
          </Tooltip>
        </Slider>
      </FormControl>
    </Box>
  );
}
export default ImageControls;
